import { useEffect, useState } from 'react'
import { countOffset, sortToObject } from 'src/common/utils'
import {
  initialRewardActivitiesFilter,
  useAuth,
  useConsumerScanStatusOptions,
  useFilter,
  useGetRewardActivities,
  usePagination,
  useRewardActivityTypeOptions,
  useCommonSettings,
  useRewardActivityChallengesOptions,
} from 'src/hooks'
import { initialtConsumerScansFilter } from 'src/hooks/scan-to-earn/use-get-consumer-scans.hook'
import { ConsumerScansList } from './consumer-scans-list'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Loader } from '../loader'
import { HelperWrapper } from '../helper-wrapper'
import { PaginationButtons, PaginationContext } from '../pagination-buttons'
import { Filter } from '../filter'
import { ETranslationNs } from 'src/types'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { MobileBottomLayout } from '../mobile-bottom-layout'
import PlusIconRounded from 'src/assets/icons/v2/PlusIconRounded'
import { ROUTES } from 'src/constants'

export const defaultLimit = 50
export const defaultPage = 0
export const defaultSort = 'rewardActivity.created_at-desc'

// Now renamed to RewardActivity instead of ScanHistory
export const ConsumerScanHistoryComponent = () => {
  const [t] = useTranslation([ETranslationNs.Scans, ETranslationNs.Navbar])

  const [searchParams] = useSearchParams()
  const isFilterOpened = searchParams.get('open')

  const [sort, setSort] = useState(defaultSort)
  const [isScans, setIsScans] = useState(true)

  const [filter, setFilter] = useFilter({ initialState: initialRewardActivitiesFilter })
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(defaultPage, defaultLimit)

  const { data: settings } = useCommonSettings()

  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const { isSuperadmin } = useAuth()

  const { data, isLoading } = useGetRewardActivities({
    ...filter,
    offset: countOffset(page, rowsPerPage),
    limit: rowsPerPage,
    ...sortToObject(sort),
  })

  useEffect(() => {
    if (!isLoading && data) {
      if (data.data.length === 0) {
        setIsScans(false)
      }

      if (data.data.length > 0) {
        setIsScans(true)
      }
    }
  }, [isLoading, data])

  useEffect(() => {
    setPage(defaultPage)
  }, [filter, setPage, sort])

  const statusSelectionOptions = useConsumerScanStatusOptions()
  const typeSelectionOptions = useRewardActivityTypeOptions()
  const challengesOptions = useRewardActivityChallengesOptions()

  const paginationValues = {
    count: data?.count ?? 0,
    page: page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!data && data.count > rowsPerPage,
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {settings?.scan_to_earn_enabled && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1,
            justifyContent: 'center',
            mb: '24px',
          }}
        >
          <Link
            to={`/${ROUTES.USER}/${ROUTES.SCAN_TO_EARN}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              textDecoration: 'none',
            }}
          >
            <PlusIconRounded isActive />
            <Box
              sx={{ fontWeight: 500, color: theme.palette.custom.primary, position: 'relative' }}
            >
              {t('scan-to-earn-header-title.v', { ns: ETranslationNs.Navbar })}
              <Typography
                sx={{
                  position: 'absolute',
                  top: '2px',
                  right: '-35px',
                  fontStyle: 'italic',
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme.palette.custom.primary,
                }}
              >
                ALPHA
              </Typography>
            </Box>
          </Link>
        </Box>
      )}
      <Filter
        sort={sort}
        setSort={setSort}
        searchFieldText={t('scan-history-search-input.v')}
        filter={filter}
        setFilter={setFilter}
        selectionOptions={[statusSelectionOptions, typeSelectionOptions, challengesOptions]}
        initialState={initialtConsumerScansFilter}
        isFilterOpened={isFilterOpened ? isFilterOpened === 'true' : false}
      />
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: '30px',
          }}
        >
          <Loader isStatic />
        </Box>
      )}

      {!isLoading && !isScans && (
        <Box sx={{ maxWidth: '500px', fontSize: '14px', mt: '8px' }}>
          <HelperWrapper t="scan-history-empty-text" />
        </Box>
      )}
      <PaginationContext.Provider value={paginationValues}>
        {!isLoading && isScans && (
          <ConsumerScansList data={data?.data} sort={sort} setSort={setSort} />
        )}
        {isSuperadmin && !isTablet && !isLoading && isScans && <PaginationButtons />}
        {isSuperadmin && isTablet && !isLoading && isScans && (
          <MobileBottomLayout>
            <PaginationButtons buttonsOnly />
          </MobileBottomLayout>
        )}
      </PaginationContext.Provider>
    </Box>
  )
}
